import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { jwtDecode }  from 'jwt-decode';
import { useConfig } from './ConfigContext';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
	const { backendApiBaseUrl } = useConfig();
  	const [isLoggedIn, setIsLoggedIn] = useState(false);
  	const [user, setUser] = useState(null);
	const [id, setId] = useState(null);
  	const [token, setToken] = useState(null);
	const [role, setRole] = useState(null);
	const [authLoaded, setAuthLoaded] = useState(false);
  	
	const checkToken = useCallback(async () => {
		const storedToken = localStorage.getItem("token");
	
		if (storedToken) {
			try {
				const parsedToken = JSON.parse(storedToken);
				const decodedToken = jwtDecode(parsedToken.token);
				const now = new Date().getTime();

				if (parsedToken.expiration && parsedToken.expiration > now) {
				
					setIsLoggedIn(true);
					setToken(parsedToken);
					setId(decodedToken.id);
					setUser(decodedToken.username);
					localStorage.setItem("user", decodedToken.username);
					
					setRole(decodedToken.role);
					
				} else {
					logout();
				}
				

			} catch (error) {
				console.error("Failed to parse token: ", error);
				logout();
			}
		}
		setAuthLoaded(true);

	}, []);
	
	useEffect(() => {
		checkToken();
	
		const interval = setInterval(() => {
			checkToken();
		}, 5 * 60 * 1000);
		
		return () => clearInterval(interval);
	}, [checkToken]); 

	
  	const login = useCallback(async (code) => {
    	try {
			const response = await fetch(`${backendApiBaseUrl}/auth/discord/redirect?code=${code}`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json"
				}
			});
		
			const data = await response.json();
			if (response.ok) {
				const decodedToken = jwtDecode(data.token);

				const newToken = {
					token: data.token,
					expiration: decodedToken.exp * 1000,
				};
	
				localStorage.setItem("token", JSON.stringify(newToken));    

				setIsLoggedIn(true);
				setToken(newToken);
				setId(decodedToken.id);  
				setUser(decodedToken.username);
				localStorage.setItem("user", decodedToken.username);
				setRole(decodedToken.role);

				return true;

			} else {
				// !response.ok
				console.error("Login failed");
				return false;
			}
		} catch (error) {
			console.error("Error logging in:", error);
			return false;
		}
	}, [backendApiBaseUrl]);

	const logout = () => {
		localStorage.removeItem("token");
		setIsLoggedIn(false);
		setRole(null);
		setId(null)
		setUser(null);
		setToken(null);
	};

	const refreshAccessToken = async () => {
		const storedToken = localStorage.getItem("token");

		if (!storedToken) {
			console.error("No storedToken found");
			return;
		}

		const jwtToken = JSON.parse(storedToken).token

		if (!jwtToken) {
			console.error("No refresh token found in stored token.");
			return;
		}

		try {
			const response = await fetch(`${backendApiBaseUrl}/auth/token/refresh`, {
				method: "POST",
				headers: {"Content-Type": "application/json"},
				body: JSON.stringify({ token: jwtToken }),
			});
		
			if (!response.ok) {
				throw new Error("Problem fetching new token");
			}

			const data = await response.json();
			// update token in state and localStorage
			const decoded = jwtDecode(data.token);
			const updatedToken = {
				token: data.token,
				expiration: jwtDecode(data.token).exp * 20000,
			};

			localStorage.setItem("token", JSON.stringify(updatedToken));
			setToken(updatedToken);
			setId(decoded.id);
			setUser(decoded.username);

		} catch (error) {
			console.error("Error refreshing token:", error);
		}
	};

	return (
		<AuthContext.Provider value={{ isLoggedIn, token, user, id, role, checkToken, login, logout, refreshAccessToken, authLoaded }}>
		  {authLoaded ? children : <div>Loading auth...</div>}
		</AuthContext.Provider>
	);
};

export const useAuth = () => useContext(AuthContext);